import { type BoxStaffItemDto, BoxStaffItemDtoSchema, StaffColumnToSort } from "@modules/boxStaff/gateway/boxStaffDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { MemberDetailedEntity } from "@modules/members/entities/memberEntity"
import { type MemberApiGateway, SESSION_HISTORY_STATUS_FILTER } from "@modules/members/gateway/memberApiGateway"
import {
  MemberColumnToSort,
  type MemberDetailedDto,
  MemberDetailedDtoSchema,
  MemberFilter,
  type MemberItemDto,
  MemberItemDtoSchema,
  type MemberSessionHistoryItemDto,
  MemberSessionHistoryItemDtoSchema,
  type MemberSessionHistorySummaryDto,
  MemberSessionHistorySummaryDtoSchema,
} from "@modules/members/gateway/memberDto"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { type Page, type Paginated, type PaginatedWrapper, toPage } from "@modules/utils/paginated"

export class MemberApi implements MemberApiGateway {
  private baseUrl = "/box/members"
  private archivedUrl = "/box/archived/members"
  private archivedStaffUrl = "/box/archived/staff-members"

  constructor(private api: ApiClient) {}

  async getMembers(
    filter: MemberFilter,
    page = 0,
    size = 10,
    nameFilter?: string,
    sortBy?: MemberColumnToSort,
    sortOrder?: ColumnSortingOrder,
  ): Promise<Page<MemberItemDto>> {
    const result = await this.api.get<Paginated<MemberItemDto>>(`${this.baseUrl}`, {
      params: { page, size, filter, name: nameFilter, columnToSort: sortBy, columnSortingOrder: sortOrder },
    })
    const pageResult = toPage(result.data)
    return {
      ...pageResult,
      content: pageResult.content.map((member) => LoggerService.parseApiDto(MemberItemDtoSchema, member)),
    }
  }

  async getMember(memberId: string): Promise<MemberDetailedDto> {
    const resultDto = await this.api.get<MemberDetailedDto>(`${this.baseUrl}/${memberId}`)
    return LoggerService.parseApiDto(MemberDetailedDtoSchema, resultDto.data)
  }

  async updateMember(memberId: string, member: MemberDetailedEntity): Promise<MemberDetailedDto> {
    const resultDto = await this.api.put<MemberDetailedDto>(`${this.baseUrl}/${memberId}`, member.getInputUpdate())
    return LoggerService.parseApiDto(MemberDetailedDtoSchema, resultDto.data)
  }

  async archiveMember(memberId: string): Promise<void> {
    return await this.api.delete(`${this.baseUrl}/${memberId}`)
  }

  async getArchivedMembers(
    page = 0,
    size = 10,
    nameFilter?: string,
    sortBy?: MemberColumnToSort,
    sortOrder?: ColumnSortingOrder,
  ): Promise<PaginatedWrapper<MemberItemDto>> {
    const result = await this.api.get<PaginatedWrapper<MemberItemDto>>(`${this.archivedUrl}`, {
      params: { page, size, name: nameFilter, columnToSort: sortBy, columnSortingOrder: sortOrder },
    })

    return {
      ...result.data,
      content: result.data.content.map((member) => LoggerService.parseApiDto(MemberItemDtoSchema, member)),
    }
  }

  async getArchivedStaffMembers(
    page = 0,
    size = 10,
    nameFilter?: string,
    sortBy?: StaffColumnToSort,
    sortOrder?: ColumnSortingOrder,
  ): Promise<PaginatedWrapper<BoxStaffItemDto>> {
    const result = await this.api.get<PaginatedWrapper<BoxStaffItemDto>>(`${this.archivedStaffUrl}`, {
      params: { page, size, name: nameFilter, columnToSort: sortBy, columnSortingOrder: sortOrder },
    })

    return {
      ...result.data,
      content: result.data.content.map((staff) => LoggerService.parseApiDto(BoxStaffItemDtoSchema, staff)),
    }
  }

  async getMemberSessionHistorySummary(
    memberId: string,
    year: number,
    month: number | null,
  ): Promise<MemberSessionHistorySummaryDto> {
    const result = await this.api.get<MemberSessionHistorySummaryDto>(`${this.baseUrl}/${memberId}/sessions/stats`, {
      params: { year, month },
    })
    return LoggerService.parseApiDto(MemberSessionHistorySummaryDtoSchema, result.data)
  }

  async getMemberSessionHistoryList(
    memberId: string,
    year: number,
    month: number | null,
    status: SESSION_HISTORY_STATUS_FILTER,
    page = 0,
    pageSize = 20,
  ): Promise<Paginated<MemberSessionHistoryItemDto>> {
    const result = await this.api.get<Paginated<MemberSessionHistoryItemDto>>(`${this.baseUrl}/${memberId}/sessions`, {
      params: {
        year,
        month,
        status: status === SESSION_HISTORY_STATUS_FILTER.ALL ? undefined : status,
        page,
        pageSize,
      },
    })
    return {
      ...result.data,
      content: result.data.content.map((item) => LoggerService.parseApiDto(MemberSessionHistoryItemDtoSchema, item)),
    }
  }
}
